// The day's numbers, as one page, from the two reads opening a show is asking for.
//
// EVERY NUMBER ON THIS SCREEN WAS CALCULATED BY xp/operator AND NONE OF IT HERE.
// Operator and opt-in are fetched in that order, not in parallel: two
// disclosures, one intent, and a deterministic request log. Export is not
// asked for. Reconciliation and the survey are links, not a third and fourth
// read: each files its own report_access row, and prefetching would file a
// disclosure nobody made.
//
// A REFUSAL OF THE OPERATOR VIEW IS THE WHOLE PAGE. Opt-in failing after that
// is a refused section, not a page of zeroes — the day's counts are still true.
//
// SLOTS THAT ARE NOT CONNECTED SAY SO. Site visits are Plausible's, asked
// after the operator view on a Nova admin's sign-in, per show over the same
// days (email is Mailgun's per-show events now, in the operator view).
// Drawing an unconfigured slot as 0 would be inventing the number the next
// person quotes. Those cards are .nc-item and the day's figures are .fig, so a
// check that counts today's six cannot mistake a "Not connected" for a count.
// The dashboard check signs in as a client viewer, so opening the dashboard
// there still makes three requests.
//
// THE ACCESS NOTE IS DRAWN RATHER THAN HIDDEN. Same argument as Operator.tsx
// used to make on its own: it is the difference between "nothing is missing"
// and "nothing is missing that I am allowed to see".
//
// THE DESIGN'S MARKUP AND ORDER (TASKS §143, step 5). The head stays on the
// page while the day is read and when it fails, with the kit's skeleton and
// Failed under it; the sections run in the prototype's order.
//
// THE RANGE (step 7). The design's range bar reads the operator view over the
// whole run, the venue's today, its last seven days or two dates; the service
// names the days, in the venue's zone, and a show with no zone is offered no
// range. The six figures are headed with what they were read over, which is
// why the heading that said "Today" over a whole run's counts is gone.
// Consent stays the whole run's whatever the range (optIn.ts's ruling), and
// the figures say so; opt-in and the traffic are read once, not per range.
// The count of records and consent and email are the design's, as far as the
// service has them; what happened to the email is Mailgun's own events, kept
// by the webhook per show and kind (mail/webhook.ts), with no registration
// confirmation row because XP sends none.

import { useEffect, useRef, useState, type ReactNode } from "react";
import { FreshnessPulse, NoticeBox, PageFreshness, usePageFreshness } from "@nova/desk-ui/parts";
import { Empty, SecLabel, Seg } from "@nova/desk-ui/kit";
import { RangeBar, type Range } from "@nova/desk-ui/metrics";
import { Loading } from "@nova/desk-ui/states";
import type { OperatorView } from "../../../operator/src/operatorView.js";
import type { OptInRates, PurposeOptIn } from "../../../operator/src/optIn.js";
import { readSiteStats, type SiteStatsReport } from "../api/admin.js";
import { readReport, type ApiOptions, type Failure } from "../api/client.js";
import { bytes, count, minutes, moment, percent, share, statusClass, TOO_FEW, UNKNOWN } from "../operator/present.js";
import { ACTIVATION_UUID_LABEL } from "./ActivationUuid.js";
import { AttClose, useDismissed } from "./Dismissed.js";
import { ReadFailed } from "./ReadFailed.js";
import { opens } from "./roleAccess.js";
import { useShow } from "./Show.js";
import type { SessionToken } from "../session/store.js";

export type DashboardProps = {
  readonly api: ApiOptions;
  readonly token: SessionToken;
  readonly activationId: string;
  readonly onSessionEnded: (detail: string) => void;
  readonly role: string;
  readonly onEnrol: () => void;
  readonly onReconciliation: () => void;
  readonly onOptIn: () => void;
  readonly onSurvey: () => void;
};

function DashboardTitle({ title }: { title: string }): ReactNode {
  const freshness = usePageFreshness();
  return (
    <>
      <div className={freshness === null ? undefined : "head-bar"}>
        <h1>{title}</h1>
        {freshness === null ? null : <PageFreshness {...freshness} />}
      </div>
      {freshness !== null && freshness.refreshing ? <FreshnessPulse /> : null}
    </>
  );
}

/** What the day reads after the operator view, once per visit. */
type Rest = {
  readonly rates: OptInRates | null;
  readonly ratesFailure: Failure | null;
};

type State =
  | { readonly phase: "reading" }
  | { readonly phase: "read"; readonly view: OperatorView }
  | { readonly phase: "failed"; readonly failure: Failure };

/**
 * The query string for the range picked, or null while Custom is missing a
 * day or runs backwards, when there is nothing to ask for yet. The whole run
 * is no query at all, which is the request this page always made.
 */
function queryFor(range: Range, from: string, to: string): string | null {
  if (range === "Today") return "?range=today";
  if (range === "Last 7 days") return "?range=last_7_days";
  if (range === "Custom") {
    if (from === "" || to === "" || from > to) return null;
    return `?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`;
  }
  return "";
}

/** What the six figures were read over, as their heading says it. */
function rangeTitle(window: OperatorView["window"] | undefined): string {
  if (window === null || window === undefined) return "The whole run";
  if (window.asked === "today") return "Today";
  if (window.asked === "last_7_days") return "Last 7 days";
  return window.from === window.to ? window.from : `${window.from} to ${window.to}`;
}

/** The same, as the words under a figure. */
function rangeWords(window: OperatorView["window"] | undefined): string {
  if (window === null || window === undefined) return "over the whole run";
  if (window.asked === "today") return "today";
  if (window.asked === "last_7_days") return "over the last 7 days";
  return window.from === window.to ? `on ${window.from}` : `from ${window.from} to ${window.to}`;
}

const RANGE_NOTE =
  "Every figure below is read when this page loads, over the range above. Nothing here updates on its own, and a range wider than the run does not invent days.";

export function Dashboard({
  api,
  token,
  activationId,
  onSessionEnded,
  role,
  onEnrol,
  onReconciliation,
  onOptIn,
  onSurvey,
}: DashboardProps): ReactNode {
  const show = useShow();
  // Whether this reader is asked Plausible's figures at all, from the one map:
  // the site-stats route is an Admin's, so an Editor or a Viewer is neither
  // asked nor shown a "Not connected" tile for a site that is connected. The
  // export switch has nothing to do with it, so it is not passed.
  const siteVisits = opens({ role, canExport: null }, "siteVisits");
  const [state, setState] = useState<State>({ phase: "reading" });
  // The opt-in rates and Ride's traffic: read once per show and per "Ask
  // again", not per range, since none of them takes one.
  const [rest, setRest] = useState<Rest | null>(null);
  // This show's site visits over the same days as the figures, read after the
  // operator view names them. A Nova admin's read: the route takes no client
  // session, so a client viewer is not asked for one and sees why.
  const [site, setSite] = useState<SiteStatsReport | null>(null);
  const [siteReading, setSiteReading] = useState(false);
  // The site read's own sequence, apart from `latest`: a consent "Ask again"
  // bumps `latest`, and sharing it left a Plausible read in flight unable to
  // land, stuck on "Reading" (Bugbot, #176). A site read gives way only to a
  // newer site read, or to the day being read again.
  const siteLatest = useRef(0);
  const restFor = useRef<string | null>(null);
  // The last day read, so the head keeps the show's name and read time while
  // another range is being read rather than dropping back to the directory's.
  const [lastView, setLastView] = useState<OperatorView | null>(null);
  // Bumped by "Ask again"; the effect below reads again whenever it changes.
  const [attempt, setAttempt] = useState(0);
  // The range (TASKS §143, step 7). The whole run is where every visit starts.
  const [range, setRange] = useState<Range>("The whole run");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  // A show with no zone is offered no range and is always asked for the whole
  // run, so a range picked before its directory row arrived cannot strand it
  // on a refusal "Ask again" would only repeat.
  const zoneKnown = show.status !== "ready" || (show.line?.time_zone ?? null) !== null;
  const query = queryFor(zoneKnown ? range : "The whole run", from, to);
  // Which read is the current one. The day's read and every consent retry take
  // a number; a read whose number is no longer the latest commits nothing, so a
  // second "Ask again", a new range, or the whole day read again, wins over an
  // older answer.
  const latest = useRef(0);
  // Leaving the page makes every read in flight stale.
  useEffect(
    () => () => {
      latest.current += 1;
    },
    [],
  );

  // Everything the day reads after the operator view: the consent rates and, on
  // Ride, the traffic figures. Shared by the first read and by "Ask again" under
  // a consent section that failed, which re-reads only this: the day's figures
  // are already on the page, and reading the operator view again would hide
  // them behind the skeleton and file another disclosure nobody asked for.
  // Null when the read was abandoned; a failure when the session ended.
  const readRest = async (isCancelled: () => boolean): Promise<Rest | Failure | null> => {
    const optIn = await readReport<OptInRates>(api, token, "optIn", activationId);
    if (isCancelled()) return null;

    if (!optIn.ok) {
      if (optIn.kind === "session_ended") {
        onSessionEnded(optIn.detail);
        return optIn;
      }
      return { rates: null, ratesFailure: optIn };
    }

    return { rates: optIn.value, ratesFailure: null };
  };

  useEffect(() => {
    if (show.status !== "ready" || query === null) return;
    let cancelled = false;
    const mine = ++latest.current;
    const stale = () => cancelled || mine !== latest.current;
    setState({ phase: "reading" });

    void (async () => {
      const operator = await readReport<OperatorView>(api, token, "operator", activationId, query);
      if (stale()) return;

      if (!operator.ok) {
        setState({ phase: "failed", failure: operator });
        if (operator.kind === "session_ended") onSessionEnded(operator.detail);
        return;
      }

      // The rest once per show and per "Ask again"; a new range alone does
      // not ask for it again.
      const restKey = `${activationId}\n${attempt}`;
      if (restFor.current !== restKey) {
        const read = await readRest(stale);
        if (read === null) return;
        if ("kind" in read) {
          setState({ phase: "failed", failure: read });
          return;
        }
        restFor.current = restKey;
        setRest(read);
      }
      setLastView(operator.value);
      setState({ phase: "read", view: operator.value });

      // AFTER THE DAY IS DRAWN, NOT BEFORE (Bugbot, #176). Plausible is a
      // vendor this page does not control, and the figures above are this
      // service's own and already here; a slow answer must not hold them
      // behind the skeleton. Until it comes, the slot says it is reading.
      if (siteVisits) {
        const window = operator.value.window ?? null;
        const siteMine = ++siteLatest.current;
        setSite(null);
        setSiteReading(true);
        const visits = await readSiteStats(
          api,
          token,
          activationId,
          window === null ? null : { from: window.from, to: window.to },
        );
        if (cancelled || siteMine !== siteLatest.current) return;
        setSiteReading(false);
        if (!visits.ok && visits.kind === "session_ended") {
          onSessionEnded(visits.detail);
          return;
        }
        setSite(visits.ok ? visits.value : null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [api, token, activationId, onSessionEnded, show.status, attempt, query, role, siteVisits]);

  // "Ask again" under the consent section: the rest of the day again, leaving
  // the figures already drawn where they are.
  const retryRates = (): void => {
    const mine = ++latest.current;
    void (async () => {
      const read = await readRest(() => mine !== latest.current);
      if (read === null) return;
      if ("kind" in read) {
        setState({ phase: "failed", failure: read });
        return;
      }
      setRest(read);
    })();
  };

  // "Ask again" asks again for whatever failed: the show's own read, which the
  // day waits on, or the day itself.
  const retry = () => (show.status === "failed" ? show.reload() : setAttempt((n) => n + 1));

  const line = show.status === "ready" ? show.line : null;
  const zone = line?.time_zone ?? null;

  // Custom starts on the run's own dates where the show has them, so the
  // first press reads the run rather than asking for two days.
  const pickRange = (next: Range): void => {
    if (next === "Custom" && from === "" && to === "") {
      setFrom(line?.opens_on ?? "");
      setTo(line?.closes_on ?? line?.opens_on ?? "");
    }
    setRange(next);
  };

  const failure = show.status === "failed" ? show.failure : state.phase === "failed" ? state.failure : null;
  const view = state.phase === "read" ? state.view : null;
  const head = view ?? lastView;
  const named = line?.client?.label;

  return (
    <div className="stack">
      {/* THREE FACTS IN A ROW RATHER THAN THREE STACKED LINES, which is the
          design's shape and is also the right one: what this is, when it was
          read and what the reader was allowed to see are one band of context
          above a page of numbers, not a paragraph somebody scrolls past. Before
          the first day is read, the head names the show from the directory the
          rail already holds, which is a read that has happened rather than a
          new one. */}
      <header className="head">
        <DashboardTitle title={head?.client?.label ?? named ?? "Today"} />
        {head === null ? null : <p className="lede">{head.client?.says ?? "Client details are unavailable."}</p>}
        <dl className="meta">
          <div>
            <dt>{ACTIVATION_UUID_LABEL}</dt>
            <dd className="mono">{head?.activation_id ?? activationId}</dd>
          </div>
          {head === null ? null : (
            <>
              <div>
                <dt>Read at</dt>
                <dd>{moment(head.as_at)}</dd>
              </div>
              <div>
                <dt>Access</dt>
                <dd>{head.access_note ?? "Access details are unavailable."}</dd>
              </div>
            </>
          )}
        </dl>
      </header>

      {show.status !== "ready" ? null : (
        <section>
          <SecLabel>What this page is reading</SecLabel>
          {zone === null ? (
            // A day is the venue's, and a show with no zone has none to name;
            // the service refuses a range for it rather than read UTC's.
            <div className="panel panel-stack">
              <p>This show has no time zone recorded, so it is read over the whole run.</p>
              <p className="help help-tight">
                Set the venue&apos;s time zone on Show details to read a day of it.
              </p>
            </div>
          ) : (
            <RangeBar
              value={range}
              onPick={pickRange}
              from={from}
              to={to}
              setFrom={setFrom}
              setTo={setTo}
              note={
                range === "Custom" && query === null
                  ? "Pick both days, the first on or before the last. Nothing is read until then."
                  : RANGE_NOTE
              }
            />
          )}
        </section>
      )}

      {failure !== null ? (
        <ReadFailed
          what="The day's figures did not come back"
          failure={failure}
          role={role}
          onEnrol={onEnrol}
          notCounted="Counts, consents and the by-hour chart are one read and it failed. A figure of nothing would be a wrong figure, so nothing is drawn."
          onRetry={retry}
        />
      ) : show.status === "ready" && query === null ? null : view === null ? (
        <Loading rows={4} cols={4} label="Reading the day…" />
      ) : (
        <DayRead
          view={view}
          askedRange={query !== null && query !== ""}
          rest={rest}
          site={siteVisits ? site : undefined}
          siteReading={siteReading}
          role={role}
          onEnrol={onEnrol}
          onOptIn={onOptIn}
          onRetryRates={retryRates}
          onReconciliation={onReconciliation}
          onSurvey={onSurvey}
        />
      )}
    </div>
  );
}

/** Everything under the range bar once the day has been read. */
function DayRead({
  view,
  askedRange,
  rest,
  site,
  siteReading,
  role,
  onEnrol,
  onOptIn,
  onRetryRates,
  onReconciliation,
  onSurvey,
}: {
  view: OperatorView;
  /** Whether a range other than the whole run was asked for. */
  askedRange: boolean;
  rest: Rest | null;
  /** Undefined where this reader is not asked for site visits, which draws no tile. */
  site: SiteStatsReport | null | undefined;
  siteReading: boolean;
  role: string;
  onEnrol: () => void;
  onOptIn: () => void;
  onRetryRates: () => void;
  onReconciliation: () => void;
  onSurvey: () => void;
}): ReactNode {
  const headline = view.headline;
  const window = view.window ?? null;
  const wholeRunOnly = new Set<string>(window?.whole_run_only ?? []);
  const consentSub = (key: string) => (window !== null && wholeRunOnly.has(key) ? "over the whole run" : undefined);
  // The design's empty state, for a range nothing happened in. The whole run
  // with nothing in it keeps its noughts, which are what an unopened stand is.
  const quiet =
    window !== null &&
    (headline?.events_counted ?? 0) === 0 &&
    (headline?.codes_requested ?? 0) === 0 &&
    (headline?.mailbox_proved ?? 0) === 0 &&
    (view.mail?.lines ?? []).length === 0;

  return (
    <>
      {/* A SERVICE OLDER THAN THIS CONSOLE ignores the range and answers the
          whole run, with no window on the answer. Said, rather than drawing
          the run's figures under the heading of the day that was asked for:
          the console ships separately from the service and can reach a
          browser first. */}
      {askedRange && window === null ? (
        <NoticeBox kind="amber" title="This service does not read a range yet">
          <p>The figures below are the whole run. A range is read once the service is updated.</p>
        </NoticeBox>
      ) : null}

      <Attention items={view.attention ?? []} deviceCount={(view.devices ?? []).length} />

      {quiet ? (
        <Empty title={window?.asked === "today" ? "Nothing has happened yet today" : "Nothing happened in this range"}>
          <p>
            No registration has been started against this activation in the range above. The stand may
            not be open, or the range may sit before the run.
          </p>
        </Empty>
      ) : (
        <>
          <section>
            <SecLabel>{rangeTitle(window)}</SecLabel>
            <div className="figs">
              <Figure label="Registrations completed" value={count(headline?.registrations_completed)} />
              <Figure label="Registrations started" value={count(headline?.registrations_started)} />
              <Figure
                label="Consents captured"
                value={count(headline?.consents_captured)}
                sub={consentSub("consents_captured")}
              />
              <Figure
                label="Consents withdrawn"
                value={count(headline?.consents_withdrawn)}
                sub={consentSub("consents_withdrawn")}
              />
              <Figure label="Check-ins" value={count(headline?.checkins)} />
              <Figure label="Corrections in force" value={count(headline?.voids_in_force)} />
            </div>
          </section>

          <RunSection series={view.by_hour} />

          <Records headline={headline} window={window} />
        </>
      )}

      <ConsentAndEmail rates={rest?.rates ?? null} mail={view.mail} window={window} />

      <MailSection mail={view.mail} />

      {site === undefined ? null : <Traffic site={site} reading={siteReading} />}

      <OptInSection
        rates={rest?.rates ?? null}
        failure={rest?.ratesFailure ?? null}
        role={role}
        onEnrol={onEnrol}
        onOptIn={onOptIn}
        onRetry={onRetryRates}
      />

      <Funnel headline={headline} />

      <section>
        <SecLabel>Tablets</SecLabel>
        <Devices devices={view.devices ?? []} />
      </section>

      <section>
        <SecLabel>Refusals</SecLabel>
        <div className="panel">
          <p>{view.rejections?.note ?? UNKNOWN}</p>
          {view.rejections?.observed === true ? (
            <p className="quiet">
              {count(view.rejections.total)} reported by the tablets themselves.
            </p>
          ) : null}
        </div>
      </section>

      {/* THE TWO OUTBOUND READS, SIDE BY SIDE RATHER THAN STACKED AS TWO CARDS.
          They are the same kind of thing — a question this page deliberately did
          not ask — and the design pairs them so that reads as one offer rather
          than as two more sections of the report. The sentence on each is the
          argument at the top of this file, said to the person about to click. */}
      <section>
        <SecLabel>More reports</SecLabel>
        <div className="asks">
          <div className="ask">
            <h3>Did everything arrive?</h3>
            <p>Check whether everything captured on the tablets reached the service.</p>
            <button type="button" className="btn btn-quiet" onClick={onReconciliation}>
              Check arrivals <span className="arw">→</span>
            </button>
          </div>
          <div className="ask">
            <h3>What people answered</h3>
            <p>See survey answers grouped by question.</p>
            <button type="button" className="btn btn-quiet" onClick={onSurvey}>
              View survey answers <span className="arw">→</span>
            </button>
          </div>
        </div>
      </section>
    </>
  );
}

/**
 * The design's count of records, as far as the service has them (Gabe, 9 Oct:
 * "build what exists"). Completed and verified are completions; started and
 * not finished is the mailboxes that asked for a passcode and never used one.
 * Withheld from files and refused a passcode are an Admin's actions on people
 * (152, decision C4), as they stand now and over the whole run, and are drawn
 * only where the service reports them. The design's test records still have
 * no row anywhere to count, so they are not drawn rather than drawn as nought.
 */
function Records({
  headline,
  window,
}: {
  headline: OperatorView["headline"] | undefined;
  window: OperatorView["window"] | null;
}): ReactNode {
  return (
    <section>
      <SecLabel>The count of records</SecLabel>
      <div className="figs">
        <Figure
          label="Registrations completed"
          value={count(headline?.registrations_completed)}
          sub={rangeWords(window)}
        />
        <Figure label="Verified addresses" value={count(headline?.verified_addresses)} sub="passcode used" />
        <Figure label="Started, not finished" value={count(headline?.codes_unproved)} sub="no passcode used" />
        {headline?.withheld_from_files === undefined ? null : (
          <Figure label="Withheld from files" value={count(headline.withheld_from_files)} sub="kept, not exported" />
        )}
        {headline?.refused_a_passcode === undefined ? null : (
          <Figure label="Refused a passcode" value={count(headline.refused_a_passcode)} sub="blocked address" />
        )}
        {headline?.erased === undefined || headline.erased === 0 ? null : (
          <Figure label="Details erased" value={count(headline.erased)} sub="still counted, nobody named" />
        )}
      </div>
    </section>
  );
}

/**
 * Consent and email, the design's three figures. The marketing and
 * client-transfer rates are the opt-in report's, over the people who were
 * asked, and always over the whole run (optIn.ts refuses a range, as a privacy
 * ruling). Undeliverable is Mailgun's: bounced for good or marked as spam,
 * over the range, and "not reported" until Mailgun reports here.
 */
function ConsentAndEmail({
  rates,
  mail,
  window,
}: {
  rates: OptInRates | null;
  mail: OperatorView["mail"] | undefined;
  window: OperatorView["window"] | null;
}): ReactNode {
  if (rates === null && mail === undefined) return null;
  const purposes = rates?.purposes ?? [];
  const marketing =
    purposes.find((purpose) => purpose.purpose === "marketing") ??
    purposes.find((purpose) => purpose.purpose === "marketing_email");
  const transfer = purposes.find((purpose) => purpose.purpose === "client_data_transfer");
  const reporting = mail !== undefined && mail.reporting_since !== null;

  return (
    <section>
      <SecLabel>Consent and email</SecLabel>
      <div className="figs">
        {rates === null ? null : (
          <>
            <RateFigure label="Marketing opt-in" purpose={marketing} />
            <RateFigure label="Client transfer" purpose={transfer} />
          </>
        )}
        {mail === undefined ? null : reporting ? (
          <Figure label="Undeliverable email" value={count(mail.undeliverable)} sub="bounced or complained" />
        ) : (
          <div className="fig">
            <span className="fig-l">Undeliverable email</span>
            <span className="fig-v">
              <span className="unk">{UNKNOWN}</span>
            </span>
            <span className="fig-s">Mailgun is not reporting here yet</span>
          </div>
        )}
      </div>
      {window === null ? null : <p className="help help-tight">{window.note}</p>}
    </section>
  );
}

/**
 * What happened to the email, the design's table, from Mailgun's own events
 * per kind of mail, the registration confirmation and follow-ups among them
 * since 160 (decision C7, 12 Oct). Every role that reads the Dashboard reads
 * this, a Viewer included; the emails themselves are set on Registration ›
 * Emails, which is Nova's. A show Mailgun has never reported
 * on says so rather than drawing a table of noughts.
 */
function MailSection({ mail }: { mail: OperatorView["mail"] | undefined }): ReactNode {
  if (mail === undefined) return null;
  return (
    <section>
      <SecLabel>What happened to the email</SecLabel>
      {mail.reporting_since === null ? (
        <div className="panel panel-stack">
          <p>Mailgun is not reporting to this service yet, so nothing is counted here.</p>
          <p className="help help-tight">The email itself still goes out. This is only what is known about it afterwards.</p>
        </div>
      ) : mail.lines.length === 0 ? (
        <div className="panel panel-stack">
          <p>No email has gone out for this show in the range above.</p>
          <p className="help help-tight">Counted since {moment(mail.reporting_since)}, when Mailgun started reporting here.</p>
        </div>
      ) : (
        <>
          <div className="tablewrap">
            <div className="tablescroll">
              <table aria-label="What happened to the email">
                <thead>
                  <tr>
                    <th scope="col">Email</th>
                    <th scope="col" className="num">Sent</th>
                    <th scope="col" className="num">Delivered</th>
                    <th scope="col" className="num">Bounced</th>
                    <th scope="col" className="num">Bounce rate</th>
                    <th scope="col" className="num">Opened</th>
                    <th scope="col" className="num">Open rate</th>
                  </tr>
                </thead>
                <tbody>
                  {mail.lines.map((line) => (
                    <tr key={line.kind}>
                      <td data-label="Email">{line.label}</td>
                      <td className="num" data-label="Sent">{count(line.sent)}</td>
                      <td className="num" data-label="Delivered">{count(line.delivered)}</td>
                      <td className="num" data-label="Bounced">{count(line.bounced)}</td>
                      <td className="num" data-label="Bounce rate">
                        {line.bounce_rate_percent === null ? (
                          <span className="unk">nothing sent</span>
                        ) : (
                          percent(line.bounce_rate_percent)
                        )}
                      </td>
                      <td className="num" data-label="Opened">{count(line.opened)}</td>
                      <td className="num" data-label="Open rate">
                        {line.open_rate_percent === null ? (
                          <span className="unk">nothing delivered</span>
                        ) : (
                          percent(line.open_rate_percent)
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </section>
  );
}

function RateFigure({ label, purpose }: { label: string; purpose: PurposeOptIn | undefined }): ReactNode {
  if (purpose === undefined) {
    return (
      <div className="fig">
        <span className="fig-l">{label}</span>
        <span className="fig-v">
          <span className="unk">not asked</span>
        </span>
        <span className="fig-s">no consent activity</span>
      </div>
    );
  }
  return <Figure label={label} value={share(purpose.rate)} sub={`${count(purpose.granted)} of ${count(purpose.asked)} asked`} />;
}

function Figure({ label, value, sub }: { label: string; value: string; sub?: string | undefined }): ReactNode {
  return (
    <div className="fig">
      <span className="fig-l">{label}</span>
      <span className="fig-v">{value}</span>
      {sub === undefined ? null : <span className="fig-s">{sub}</span>}
    </div>
  );
}

/**
 * Registrations across the run, as a chart or as the same numbers in a table.
 *
 * THE TABLE IS NOT AN ACCESSIBILITY AFTERTHOUGHT, IT IS THE OTHER QUESTION.
 * A chart answers "what shape was the day" and a table answers "how many, on
 * which day", and the crew asks both. The chart carries an aria-label naming
 * every point for a reader who cannot see it, so the toggle is a preference
 * rather than a ramp: nobody is sent to the table to find out what happened.
 *
 * NOTHING IS DRAWN FOR A SINGLE HOUR. A line between one point and itself is a
 * flat segment implying a trend nobody measured, and the six figures above
 * already say the total. One hour is a number, and the number is already there.
 */
function RunSection({ series }: { series: OperatorView["by_hour"] | undefined }): ReactNode {
  const [shown, setShown] = useState<"Graph" | "Table">("Graph");

  // THE TYPE IS A PROMISE THE SERVICE MAY NOT HAVE BEEN DEPLOYED TO KEEP.
  // readReport is a bare cast: it names the shape it expects and nothing checks
  // that the body matches, so every field on OperatorView is a claim about a
  // service that ships separately from this bundle. by_hour is new, and a
  // console that reached the browser first met an operator view without it,
  // read .days off undefined, and took the whole page down to white with it.
  // The dashboard is the screen behind every activation, so that was every
  // activation.
  //
  // Guarded here rather than by validating the whole payload on the way in,
  // which is the better fix and a bigger one: it needs a parser per report and
  // a decision about what a screen does with a body that fails it. What this
  // does buy is the right failure. A section that cannot be drawn is missing,
  // and the six figures above it, the attention list and the devices table are
  // all still on the page.
  if (series === undefined || !Array.isArray(series.days) || !Array.isArray(series.hours)) return null;
  if (series.days.length === 0 || series.hours.length === 0) return null;

  const known = series.hours_from === "opening_hours";

  return (
    <section>
      <SecLabel>Registrations over the run</SecLabel>
      <div className="panel panel-stack">
        <div className="filters">
          <div className="field">
            <span className="label">Shown as</span>
            <Seg label="Shown as" value={shown} options={["Graph", "Table"] as const} onPick={setShown} />
          </div>
        </div>
        {shown === "Graph" ? (
          <RunChart
            series={series}
            unit={
              known
                ? "Each line is one day of the run, over the hall's opening hours, so a line that stops early is a day that ended early."
                : "Opening hours are not set, so this chart only shows hours with registrations."
            }
          />
        ) : (
          <RunTable series={series} />
        )}
      </div>
    </section>
  );
}

/**
 * Geometry from handoff/console-metrics.jsx. A fixed viewBox scaled by CSS
 * rather than measured in JS: the panel is fluid, and a chart that re-reads its
 * own width on every resize is a second layout pass bought for nothing here.
 *
 * THE LINES ARE TOLD APART BY DASH AND NOT BY HUE, which is the design's rule
 * and the reason its stylesheet ships `ch-s1` through `ch-s3` as dash patterns.
 * Three greys at one lightness are three identical lines to a colour-blind
 * reader and on every printout of this page. The legend keys carry the same
 * dash so the key and the line are matched by shape.
 */
function RunChart({ series, unit }: { series: OperatorView["by_hour"]; unit: string }): ReactNode {
  const W = 660;
  const H = 240;
  const L = 46;
  const R = 26;
  const T = 18;
  const B = 34;

  const seen = series.days.flatMap((day) => day.values.filter((v): v is number => v !== null));
  const top = Math.max(1, ...seen);
  const step = (W - L - R) / Math.max(1, series.hours.length - 1);
  const px = (i: number) => L + i * step;
  const py = (v: number) => T + (1 - v / top) * (H - T - B);

  return (
    <div className="chart">
      <div className="chart-h">
        <p className="chart-t">Registrations completed, by hour</p>
        <p className="chart-a">
          {series.hours[0]}:00 to {series.hours[series.hours.length - 1]}:59
        </p>
      </div>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label={
          "Registrations completed by hour. " +
          series.days
            .map(
              (day) =>
                `${day.name}, ${day.date}: ` +
                day.values
                  .map((v, i) => `${series.hours[i]}:00 ${v === null ? "hall shut" : v}`)
                  .join(", "),
            )
            .join(". ")
        }
      >
        {[0, 0.5, 1].map((tick) => (
          <g key={tick}>
            <line className="ch-grid" x1={L} x2={W - R} y1={py(top * tick)} y2={py(top * tick)} />
            <text className="ch-ax" x={L - 10} y={py(top * tick) + 4} textAnchor="end">
              {count(Math.round(top * tick))}
            </text>
          </g>
        ))}
        {series.hours.map((hour, i) => (
          <text className="ch-ax" key={hour} x={px(i)} y={H - 10} textAnchor="middle">
            {hour}
          </text>
        ))}
        {series.days.map((day, index) => (
          <g key={day.date} className={`ch-s ch-s${index}`}>
            {/* One polyline per unbroken run rather than one for the day. A null
                is an hour the hall was shut, and joining across it would draw a
                line through a time the stand did not exist. An hour stranded
                between two shut ones therefore gets a dot and no line, which is
                exactly what is true about it. */}
            {runs(day.values).map((run) => (
              <polyline
                key={run[0]?.at ?? -1}
                className="ch-line"
                points={run.map((point) => `${px(point.at)},${py(point.value)}`).join(" ")}
              />
            ))}
            {day.values.map((value, i) =>
              value === null ? null : (
                <circle className="ch-dot" key={i} cx={px(i)} cy={py(value)} r="2.6" />
              ),
            )}
          </g>
        ))}
      </svg>
      <div className="ch-legend">
        {series.days.map((day, index) => (
          <span key={day.date} className={`ch-key ch-s${index}`}>
            <svg viewBox="0 0 26 8" aria-hidden="true" focusable="false">
              <line className="ch-line" x1="1" y1="4" x2="25" y2="4" />
            </svg>
            {day.name}
          </span>
        ))}
      </div>
      <p className="help" style={{ margin: "4px 0 0" }}>{unit}</p>
    </div>
  );
}

/**
 * The stretches of a day that actually have a figure, as index/value pairs.
 * Splitting on null is what stops the chart drawing a line across hours the
 * hall was shut.
 */
function runs(values: Array<number | null>): Array<Array<{ at: number; value: number }>> {
  const out: Array<Array<{ at: number; value: number }>> = [];
  let run: Array<{ at: number; value: number }> = [];
  values.forEach((value, at) => {
    if (value === null) {
      if (run.length > 0) out.push(run);
      run = [];
      return;
    }
    run.push({ at, value });
  });
  if (run.length > 0) out.push(run);
  return out;
}

function RunTable({ series }: { series: OperatorView["by_hour"] }): ReactNode {
  return (
    <div className="tablewrap">
      <div className="tablescroll">
        <table>
          <thead>
            <tr>
              <th scope="col">Day</th>
              <th scope="col">Date</th>
              <th scope="col">Completed</th>
              <th scope="col">Busiest hour</th>
            </tr>
          </thead>
          <tbody>
            {series.days.map((day) => {
              const open = day.values
                .map((value, at) => ({ at, value }))
                .filter((point): point is { at: number; value: number } => point.value !== null);
              const total = open.reduce((sum, point) => sum + point.value, 0);
              // An empty day has no busiest hour, and picking the first one
              // would name an hour nothing happened in as the peak.
              const busiest = total === 0 ? null : open.reduce((a, b) => (b.value > a.value ? b : a));
              return (
                <tr key={day.date}>
                  <td data-label="Day">{day.name}</td>
                  <td data-label="Date">{day.date}</td>
                  <td className="num" data-label="Completed">
                    {count(total)}
                  </td>
                  <td className="num" data-label="Busiest hour">
                    {busiest === null ? (
                      <span className="unk">nothing yet</span>
                    ) : (
                      `${series.hours[busiest.at]}:00`
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Funnel({
  headline,
}: {
  headline: OperatorView["headline"] | undefined;
}): ReactNode {
  const codes = headline?.codes_requested;
  const proved = headline?.mailbox_proved;
  if (typeof codes !== "number" || typeof proved !== "number") return null;

  return (
    <section>
      <SecLabel info="Codes requested and mailboxes proved are counted from verification, separately from registrations started. Tablets write a start when the form appears. The website writes a completion without a start, so those two columns are different measurements.">
        Registration funnel
      </SecLabel>
      <div className="nc">
        <LiveStat label="Codes requested" value={count(codes)} detail="Distinct mailboxes that asked for a passcode" />
        <LiveStat label="Mailboxes proved" value={count(proved)} detail="Typed the code from the email" />
      </div>
    </section>
  );
}

/**
 * Site visits, the one figure on this page the service does not count itself.
 *
 * EMAIL USED TO SIT BESIDE IT, as Mailgun's figure for the whole estate over
 * seven days, read only on Ride. What happened to the email is counted per
 * show from Mailgun's own events now, in its table above, so that slot went.
 *
 * PER SHOW SINCE 9 OCT 2026 (Gabe). Ride has a Plausible site of its own; every
 * other show is the registration pages under its id, which they started
 * carrying that day, so a show's visits before it are not counted here. Read
 * over the same days as the figures above, on a Nova admin's sign-in.
 */
function Traffic({
  site,
  reading,
}: {
  site: SiteStatsReport | null;
  reading: boolean;
}): ReactNode {
  const siteReady = site?.configured === true && site.visitors !== null;

  return (
    <section>
      <SecLabel>{siteReady ? "Site visits" : "Not connected"}</SecLabel>
      {/* THE NOT-CONNECTED EXPLAINER WENT WITH THE OCTOBER HAND-OFF, which draws
          that section as its tiles and nothing else: "Not connected" on the
          tile is the sentence. What stays is where the figure comes from once
          there is one, and the line that says a read is in flight, because a
          tile reading "Not connected" while the read is still out would be
          a claim the page has not yet earned. */}
      {siteReady && site ? (
        <p className="help">
          {site.site === "game.novaexhibits.com"
            ? "Plausible on game.novaexhibits.com. A missing figure is missing, not a nought."
            : `Plausible on ${site.site}, this show's registration pages only, counted since they began naming their show on 9 October 2026. A missing figure is missing, not a nought.`}
        </p>
      ) : reading ? (
        <p className="help">Reading this show&apos;s site visits from Plausible…</p>
      ) : null}
      <div className="nc">
        {siteReady && site ? (
          <LiveStat
            label="Site visits"
            value={`${count(site.visitors)} visitors`}
            detail={`${count(site.pageviews)} pageviews · ${site.window}`}
          />
        ) : (
          <Pending label="Site visits" />
        )}
      </div>
    </section>
  );
}

/**
 * A figure this page reads from somewhere other than the operator view, in the
 * design's funnel cell: what it is, the number, and what the number counts.
 */
function LiveStat({
  label,
  value,
  detail,
}: {
  label: string;
  value: string;
  detail: string;
}): ReactNode {
  return (
    <div className="n">
      <p className="k">{label}</p>
      <p className="v num">{value}</p>
      <p className="d">{detail}</p>
    </div>
  );
}

function Pending({ label }: { label: string }): ReactNode {
  return (
    <div className="nc-item">
      <span className="k">{label}</span>
      <span className="v">Not connected</span>
    </div>
  );
}

function OptInSection({
  rates,
  failure,
  role,
  onEnrol,
  onOptIn,
  onRetry,
}: {
  rates: OptInRates | null;
  failure: Failure | null;
  role: string;
  onEnrol: () => void;
  onOptIn: () => void;
  onRetry: () => void;
}): ReactNode {
  if (failure !== null) {
    return (
      <section>
        <SecLabel>What people agreed to</SecLabel>
        <ReadFailed
          what="Could not load consent"
          failure={failure}
          role={role}
          onEnrol={onEnrol}
          notCounted="The dashboard figures above are unchanged."
          onRetry={onRetry}
        />
      </section>
    );
  }

  if (rates === null) return null;

  const purposes = rates.purposes ?? [];

  return (
    <section>
      <SecLabel>What people agreed to</SecLabel>
      <PurposeRates purposes={purposes} />
      {(rates.unobserved?.purposes ?? []).length === 0 ? null : (
        <NoticeBox kind="amber" title={`No consent activity for ${rates.unobserved.purposes.join(", ")}`}>
          <p>{rates.unobserved?.note ?? ""}</p>
        </NoticeBox>
      )}
      <div className="actions">
        <button type="button" className="btn btn-quiet" onClick={onOptIn}>
          The full consent figures <span className="arw">→</span>
        </button>
      </div>
    </section>
  );
}

function PurposeRates({ purposes }: { purposes: PurposeOptIn[] }): ReactNode {
  if (purposes.length === 0) {
    return (
      <div className="panel">
        <p style={{ margin: 0 }}>
          No consent activity has been recorded. No percentage is shown because missing data is not
          the same as 0%.
        </p>
      </div>
    );
  }

  return (
    <div className="purposes">
      {purposes.map((purpose) => {
        const rate = share(purpose.rate);
        const withheld = rate === TOO_FEW;
        const doubt = DOUBT[purpose.reading];
        return (
          <div key={purpose.purpose} className={doubt === undefined ? "purpose" : "purpose doubt"}>
            <div>
              <p className="p-key">{purpose.purpose}</p>
              {/* THE RATE IS ITS OWN LINE, AND SO IS THE REFUSAL TO GIVE ONE.
                  Where there are too few people to make a percentage mean
                  anything it is drawn as "too few to say", in the class that
                  says a figure is being withheld, so it is a statement rather
                  than a gap. */}
              <p className={`p-rate${doubt === undefined ? "" : " is-doubt"}${withheld ? " is-withheld" : ""}`}>{rate}</p>
              {doubt === undefined ? null : <p className="p-tag">{doubt}</p>}
            </div>
            <div>
              <p className="p-said">{purpose.says}</p>
              <p className="p-count">
                {count(purpose.granted)} agreed
                {withheld ? ` · ${TOO_FEW}` : ""}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * The tag under a rate that wants doubting, where the only interesting cases
 * are the ones that look like news. Same argument OptIn.tsx makes: nearly
 * everybody is an alarm, not a success, and nearly nobody is a control to go
 * and look at before it is a verdict on the wording.
 */
const DOUBT: Record<string, string> = {
  nearly_everyone: "worth doubting",
  almost_nobody: "almost nobody",
};

function attentionKey(item: OperatorView["attention"][number]): string {
  return `${item.severity}\n${item.device_id ?? ""}\n${item.what}`;
}

function Attention({
  items,
  deviceCount,
}: {
  items: OperatorView["attention"];
  deviceCount: number;
}): ReactNode {
  const { has } = useDismissed();
  if (items.length === 0) {
    return (
      <NoticeBox kind="good" title={deviceCount === 0 ? "No tablets connected" : "Everything looks ready"}>
        <p>
          {deviceCount === 0
            ? "Website registrations are not tablets. None has enrolled or contacted the service yet."
            : "All tablets are reporting and their queues look normal."}
        </p>
      </NoticeBox>
    );
  }

  const allAway = items.every((item) => has(attentionKey(item)));

  return (
    <section hidden={allAway}>
      <SecLabel>Needs attention</SecLabel>
      <ul className="att">
        {items.map((item, index) => {
          const id = attentionKey(item);
          return (
            <li key={index} hidden={has(id)}>
              <AttClose id={id} />
              <span className="sev">{item.severity}</span>
              <p className="att-what">{item.what}</p>
              <p className="att-do">{item.do_this}</p>
              {item.device_id === null ? null : <p className="id">{item.device_id}</p>}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function Devices({ devices }: { devices: OperatorView["devices"] }): ReactNode {
  if (devices.length === 0) {
    return (
      <div className="panel">
        <p style={{ margin: 0 }}>No tablets have connected yet.</p>
      </div>
    );
  }

  return (
    <div className="tablewrap">
      <div className="tablescroll">
        {/* Named rather than styled: the dashboard draws two tables, and
            check.mjs counts the rows of this one. */}
        <table aria-label="Tablets">
          <thead>
            <tr>
              <th scope="col">Tablet</th>
              <th scope="col">Status</th>
              <th scope="col">Last heard</th>
              <th scope="col">Counted</th>
              <th scope="col">Waiting to send</th>
              <th scope="col">Battery</th>
              <th scope="col">Free space</th>
              <th scope="col">Config</th>
            </tr>
          </thead>
          <tbody>
            {devices.map((device) => (
              // `silent` draws the accent the design puts down the side of a
              // tablet nothing is arriving from: one gone quiet for long enough
              // to look at, or one that has never been heard from at all, which
              // is the row the design draws it on. It is the same judgement the
              // attention list above already made about the same device, so the
              // two cannot disagree: both read `status`.
              <tr
                key={device.device_id}
                className={device.status === "silent" || device.status === "unknown" ? "silent" : undefined}
              >
                <td data-label="Tablet">
                  <p className="t-id">{device.device_id}</p>
                  <p className="t-note">{device.says}</p>
                </td>
                <td data-label="Status">
                  <span className={statusClass(device.status)}>{device.status}</span>
                  {device.revoked_at === null ? null : (
                    <span className="cell-sub">revoked {moment(device.revoked_at)}</span>
                  )}
                </td>
                <td className="num" data-label="Last heard">
                  <Unknownable text={minutes(device.minutes_since_last_contact)} />
                </td>
                <td className="num" data-label="Counted">
                  {count(device.events_counted)}
                </td>
                <td className="num" data-label="Waiting to send">
                  <Unknownable text={count(device.queue_depth)} />
                </td>
                <td className="num" data-label="Battery">
                  <Unknownable text={percent(device.battery_percent)} />
                </td>
                <td className="num" data-label="Free space">
                  <Unknownable text={bytes(device.storage_free_bytes)} />
                </td>
                <td className="num" data-label="Config">
                  <Unknownable text={count(device.config_version)} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Unknownable({ text }: { text: string }): ReactNode {
  return text === UNKNOWN ? <span className="unk">{text}</span> : <>{text}</>;
}
